import React from 'react';
import styled from '@emotion/styled';
import { Box, Group, Stack, Text, Tooltip } from '@mantine/core';

// Type definitions
interface ChartDataItem {
  month: string;
  value: number;
  profit?: number;
}

interface ProfitChartProps {
  data?: ChartDataItem[];
  title?: string;
  subtitle?: string;
  percentage?: number;
  height?: number;
  colors?: string[];
}

// Styled components using Emotion to match exactly the screenshot
const ChartContainer = styled.div`
  background: #ee374d; /* Solid vibrant red from image */
  border-radius: 24px;
  padding: 24px 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  font-family: 'Inter', sans-serif;
  height: 100%;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: none;
`;

const ChartHeader = styled.div`
  margin-bottom: 20px;
`;

const ChartTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.5px;
`;

const ChartSubtitle = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.8);
  margin: 4px 0 0 0;
  font-weight: 500;
`;

const ChartContent = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  width: 100%;
  margin-top: auto;
`;

const BarsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex: 1;
`;

const BarColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const BarTrack = styled.div<{ height: number }>`
  width: 22px;
  height: ${props => props.height}px;
  background-color: white;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const BarFill = styled.div<{ fillValue: number }>`
  width: 100%;
  height: ${props => Math.min(90, Math.abs(props.fillValue) * 0.8)}%;
  background-color: ${props => props.fillValue <= 0 ? '#fbd971' : "#c1fb71ff"};
  border-radius: 6px 6px 0 0;
  transition: height 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  margin-top: auto;
`;

const BarLabel = styled.span`
  font-size: 11px;
  color: white;
  font-weight: 600;
  text-transform: capitalize;
`;

const HugePercentage = styled.div`
  font-size: 34px;
  font-weight: 500;
  color: white;
  line-height: 1;
  margin-left: 20px;
  letter-spacing: -1px;
`;

const ProfitChart: React.FC<ProfitChartProps> = ({
  data = [],
  title = "Monthly Profit",
  subtitle = "Net Income after Expenses",
  percentage = 75,
  height = 90,
  colors = ['#fbd971']
}) => {
  const defaultData: ChartDataItem[] = [
    { month: 'Jan', value: 35 },
    { month: 'Feb', value: 20 },
    { month: 'Mar', value: 55 },
    { month: 'Apr', value: 85 },
    { month: 'May', value: 35 },
    { month: 'Jun', value: 0 },
    { month: 'Jul', value: 0 }
  ];

  const chartData: ChartDataItem[] = data.length > 0 ? data : defaultData;
  const maxValue = 100; // Assuming 100% scale based on image behavior

  return (
    <ChartContainer>
      <ChartHeader>
        <ChartTitle>{title}</ChartTitle>
        <ChartSubtitle>{subtitle}</ChartSubtitle>
      </ChartHeader>

      <ChartContent>
        <BarsContainer>
          {chartData.map((item, index) => (
            <BarColumn key={index}>
              <Tooltip
                label={`₹${(item.profit || 0).toLocaleString('en-IN')}`}
                position="top"
                withArrow
                withinPortal
              >
                <BarTrack height={height} style={{ cursor: 'pointer' }}>
                  <BarFill fillValue={item.value} />
                </BarTrack>
              </Tooltip>
              <BarLabel>{item.month}</BarLabel>
            </BarColumn>
          ))}
        </BarsContainer>

        <HugePercentage style={{ marginBottom: "56px" }}>
          {percentage}%
        </HugePercentage>
      </ChartContent>
    </ChartContainer>
  );
};

export type { ChartDataItem, ProfitChartProps };
export default ProfitChart;